import { useState, useEffect } from 'react'
import { createClient } from '../api/clients'
import { createDeal } from '../api/deals'
import { listClients } from '../api/clients'
import { pb } from '../lib/pocketbase'
import type { DealStage } from '../types'

// ── Trigger hook ──────────────────────────────────────────────────────────────

const ONBOARDING_DONE_KEY = 'reppr_onboarding_done'

export function useOnboardingWizard() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (localStorage.getItem(ONBOARDING_DONE_KEY)) return

    const timer = setTimeout(async () => {
      // Only show if user has no clients yet
      try {
        const clients = await listClients({ limit: 1 })
        if (clients.length === 0) {
          setShow(true)
        }
      } catch {
        // If we can't check, don't show — don't crash
      }
    }, 2000)

    return () => clearTimeout(timer)
  }, [])

  const dismiss = () => {
    localStorage.setItem(ONBOARDING_DONE_KEY, '1')
    setShow(false)
  }

  return { show, dismiss }
}

// ── Component ─────────────────────────────────────────────────────────────────

interface OnboardingWizardProps {
  onComplete: () => void
}

type Stage = 'Prospect' | 'Negotiating' | 'Agreed'

export default function OnboardingWizard({ onComplete }: OnboardingWizardProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1)

  // Step 1 state
  const [clientName, setClientName] = useState('')
  const [clientSport, setClientSport] = useState('')
  const [clientLoading, setClientLoading] = useState(false)
  const [clientError, setClientError] = useState<string | null>(null)
  const [createdClientId, setCreatedClientId] = useState<string | null>(null)

  // Step 2 state
  const [dealTitle, setDealTitle] = useState('')
  const [dealValue, setDealValue] = useState('')
  const [dealStage, setDealStage] = useState<Stage>('Prospect')
  const [dealLoading, setDealLoading] = useState(false)
  const [dealError, setDealError] = useState<string | null>(null)

  const user = pb.authStore.model as any
  const agencyId: string | undefined = user?.agency_id
  const userId: string = user?.id ?? ''

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleAddClient = async () => {
    if (!clientName.trim()) {
      setClientError('Full name is required.')
      return
    }
    setClientLoading(true)
    setClientError(null)
    try {
      const payload: Record<string, any> = { full_name: clientName.trim() }
      if (clientSport.trim()) payload.sport = clientSport.trim()
      if (agencyId) payload.agency_id = agencyId
      const created = await createClient(payload, userId)
      setCreatedClientId(created.id)
      setStep(2)
    } catch (err: any) {
      setClientError(err.message || 'Failed to create client.')
    } finally {
      setClientLoading(false)
    }
  }

  const handleCreateDeal = async () => {
    if (!dealTitle.trim()) {
      setDealError('Deal title is required.')
      return
    }
    if (!createdClientId) {
      setDealError('No client associated. Please go back and add a client.')
      return
    }
    setDealLoading(true)
    setDealError(null)
    try {
      const payload: any = {
        title: dealTitle.trim(),
        deal_type: 'other' as const,
        stage: dealStage as DealStage,
        client_id: createdClientId,
      }
      if (dealValue.trim() !== '') {
        const parsed = parseFloat(dealValue)
        if (!isNaN(parsed)) payload.value = parsed
      }
      if (agencyId) payload.agency_id = agencyId
      await createDeal(payload, userId)
      setStep(3)
    } catch (err: any) {
      setDealError(err.message || 'Failed to create deal.')
    } finally {
      setDealLoading(false)
    }
  }

  // ── Styles ───────────────────────────────────────────────────────────────────

  const overlay: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.65)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const modal: React.CSSProperties = {
    background: '#1e1e2e',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 0,
    width: '100%',
    maxWidth: '480px',
    padding: '32px',
    position: 'relative',
    color: '#EFEDE8',
    fontFamily: 'inherit',
  }

  const progressRow: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    marginBottom: '28px',
  }

  const progressDot = (active: boolean, done: boolean): React.CSSProperties => ({
    flex: 1,
    height: '4px',
    borderRadius: 0,
    background: done ? '#C8A45C' : active ? '#C8A45C' : 'rgba(255,255,255,0.12)',
    opacity: done || active ? 1 : 0.5,
    transition: 'background 0.2s',
  })

  const heading: React.CSSProperties = {
    fontSize: '20px',
    fontWeight: 600,
    margin: '0 0 6px 0',
    color: '#EFEDE8',
  }

  const subtext: React.CSSProperties = {
    fontSize: '14px',
    color: 'rgba(244,244,245,0.55)',
    margin: '0 0 24px 0',
  }

  const label: React.CSSProperties = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: 'rgba(244,244,245,0.7)',
    marginBottom: '6px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  }

  const input: React.CSSProperties = {
    width: '100%',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 0,
    padding: '10px 12px',
    color: '#EFEDE8',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    marginBottom: '14px',
  }

  const primaryBtn: React.CSSProperties = {
    width: '100%',
    background: '#C8A45C',
    color: '#fff',
    border: 'none',
    borderRadius: 0,
    padding: '11px 0',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '8px',
  }

  const primaryBtnDisabled: React.CSSProperties = {
    ...primaryBtn,
    opacity: 0.5,
    cursor: 'not-allowed',
  }

  const skipLink: React.CSSProperties = {
    display: 'block',
    textAlign: 'center' as const,
    marginTop: '14px',
    fontSize: '13px',
    color: 'rgba(244,244,245,0.45)',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    textDecoration: 'underline',
    width: '100%',
  }

  const errorMsg: React.CSSProperties = {
    fontSize: '13px',
    color: '#ff6b6b',
    marginBottom: '10px',
  }

  const dismissLink: React.CSSProperties = {
    position: 'absolute',
    top: '16px',
    right: '20px',
    fontSize: '12px',
    color: 'rgba(244,244,245,0.35)',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    textDecoration: 'underline',
  }

  const bulletList: React.CSSProperties = {
    listStyle: 'none',
    padding: 0,
    margin: '18px 0 28px 0',
  }

  const bulletItem: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    fontSize: '14px',
    color: 'rgba(244,244,245,0.8)',
    marginBottom: '12px',
  }

  const bulletDot: React.CSSProperties = {
    width: '6px',
    height: '6px',
    borderRadius: 0,
    background: '#C8A45C',
    flexShrink: 0,
    marginTop: '6px',
  }

  // ── Render steps ─────────────────────────────────────────────────────────────

  return (
    <div style={overlay}>
      <div style={modal}>
        {/* Global dismiss */}
        <button style={dismissLink} onClick={onComplete} type="button">
          I'll set up later
        </button>

        {/* Progress indicator */}
        <div style={progressRow}>
          <div style={progressDot(step === 1, step > 1)} />
          <div style={progressDot(step === 2, step > 2)} />
          <div style={progressDot(step === 3, false)} />
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <>
            <h2 style={heading}>Add your first client</h2>
            <p style={subtext}>Start building your roster. You can add more details from the client's page.</p>

            <label style={label} htmlFor="ob-client-name">Full Name *</label>
            <input
              id="ob-client-name"
              style={input}
              type="text"
              placeholder="e.g. Marcus Johnson"
              value={clientName}
              onChange={e => setClientName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddClient()}
              autoFocus
            />

            <label style={label} htmlFor="ob-client-sport">Sport</label>
            <input
              id="ob-client-sport"
              style={input}
              type="text"
              placeholder="e.g. Football, Basketball"
              value={clientSport}
              onChange={e => setClientSport(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddClient()}
            />

            {clientError && <p style={errorMsg}>{clientError}</p>}

            <button
              style={clientLoading ? primaryBtnDisabled : primaryBtn}
              onClick={handleAddClient}
              disabled={clientLoading}
              type="button"
            >
              {clientLoading ? 'Adding...' : 'Add Client'}
            </button>

            <button
              style={skipLink}
              onClick={() => setStep(2)}
              type="button"
            >
              Add clients later
            </button>
          </>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <>
            <h2 style={heading}>Create your first deal</h2>
            <p style={subtext}>
              Track an endorsement, contract negotiation, or any opportunity in your pipeline.
            </p>

            <label style={label} htmlFor="ob-deal-title">Deal Title *</label>
            <input
              id="ob-deal-title"
              style={input}
              type="text"
              placeholder="e.g. Nike Endorsement"
              value={dealTitle}
              onChange={e => setDealTitle(e.target.value)}
              autoFocus
            />

            <label style={label} htmlFor="ob-deal-value">Value (optional)</label>
            <input
              id="ob-deal-value"
              style={input}
              type="number"
              placeholder="e.g. 500000"
              value={dealValue}
              onChange={e => setDealValue(e.target.value)}
            />

            <label style={label} htmlFor="ob-deal-stage">Stage</label>
            <select
              id="ob-deal-stage"
              style={{ ...input, marginBottom: '14px' }}
              value={dealStage}
              onChange={e => setDealStage(e.target.value as Stage)}
            >
              <option value="Prospect">Prospect</option>
              <option value="Negotiating">Negotiating</option>
              <option value="Agreed">Agreed</option>
            </select>

            {!createdClientId && (
              <p style={{ ...subtext, fontSize: '12px', marginBottom: '12px', color: 'rgba(244,244,245,0.4)' }}>
                No client linked — skipped client setup. You can link a client from the deal page.
              </p>
            )}

            {dealError && <p style={errorMsg}>{dealError}</p>}

            <button
              style={dealLoading || !createdClientId ? primaryBtnDisabled : primaryBtn}
              onClick={handleCreateDeal}
              disabled={dealLoading || !createdClientId}
              type="button"
            >
              {dealLoading ? 'Creating...' : 'Create Deal'}
            </button>

            <button
              style={skipLink}
              onClick={() => setStep(3)}
              type="button"
            >
              Create deals later
            </button>
          </>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <>
            <h2 style={heading}>You're set up</h2>
            <p style={subtext}>Your agency is ready. Here's what to know as you get started.</p>

            <ul style={bulletList}>
              <li style={bulletItem}>
                <span style={bulletDot} />
                <span>Quick Log (Cmd+L) to log any call in 20 seconds</span>
              </li>
              <li style={bulletItem}>
                <span style={bulletDot} />
                <span>Send portal invites from any client's page</span>
              </li>
            </ul>

            <button
              style={primaryBtn}
              onClick={onComplete}
              type="button"
            >
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  )
}
